<script setup lang="ts">
import {nextTick, onMounted, onUnmounted, ref, watch} from 'vue';
import NamaIcon from '@/components/icons/NamaIcon.vue';
import Translator from '@/utils/common/Translator.ts';
import ChatUtils from '@/utils/common/ChatUtils.ts';
import type {SearchItem} from '@/utils/common/TopPanelSearchUtils.ts';

const props = defineProps<{
  items: SearchItem[];
  phase: 'member' | 'entity' | 'record';
  searching: boolean;
}>();

const emit = defineEmits<{
  select: [item: SearchItem];
  close: [];
}>();

const highlightedIndex = ref(0);
const listRef = ref<HTMLElement | null>(null);
const itemRefs = ref<(HTMLElement | null)[]>([]);

function handleKey(e: KeyboardEvent) {
  const len = props.items.length;
  if (e.key === 'ArrowDown') {
    e.preventDefault();
    e.stopPropagation();
    if (len > 0) {
      highlightedIndex.value = (highlightedIndex.value + 1) % len;
      scrollToHighlighted();
    }
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    e.stopPropagation();
    if (len > 0) {
      highlightedIndex.value = (highlightedIndex.value - 1 + len) % len;
      scrollToHighlighted();
    }
  } else if (e.key === 'Enter' || e.key === 'Tab') {
    e.preventDefault();
    e.stopPropagation();
    if (len > 0 && highlightedIndex.value < len) {
      emit('select', props.items[highlightedIndex.value]);
    }
  }
}

function scrollToHighlighted() {
  nextTick(() => {
    const el = itemRefs.value[highlightedIndex.value];
    if (el) el.scrollIntoView({block: 'nearest', behavior: 'smooth'});
  });
}

watch(() => props.items, () => {
  highlightedIndex.value = 0;
  itemRefs.value = [];
});

function getMemberInitials(item: SearchItem): string {
  const name = item.name1 || item.name2 || item.code || '?';
  const words = name.trim().split(/\s+/);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
}

function getAvatarStyle(item: SearchItem) {
  const s = ChatUtils.MENTION_ENTITY_STYLES[ChatUtils.mentionItemKey(item, 0).length % ChatUtils.MENTION_ENTITY_STYLES.length];
  return {
    backgroundColor: s.bg, color: s.text,
    width: '28px', height: '28px', borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '11px', fontWeight: '600', flexShrink: '0',
  };
}

function sectionAt(idx: number): 'members' | 'entities' | null {
  if (props.phase !== 'member') return null;
  const item = props.items[idx];
  const prev = idx > 0 ? props.items[idx - 1] : null;
  if (item.type === 'user' && (!prev || prev.type !== 'user')) return 'members';
  if (item.type !== 'user' && (!prev || prev.type === 'user')) return 'entities';
  return null;
}

onMounted(() => window.addEventListener('keydown', handleKey, true));
onUnmounted(() => window.removeEventListener('keydown', handleKey, true));
</script>

<template>
  <div class="mention-dropdown">
    <div class="mention-dropdown-header">
      <span class="mention-dropdown-title" v-text="ChatUtils.getMentionDropdownTitle(phase)"/>
      <span v-if="!searching && items.length > 0" class="mention-dropdown-count" v-text="items.length"/>
    </div>
    <div v-if="searching" class="tw-flex tw-items-center tw-justify-center tw-py-4">
      <q-spinner-dots size="1.5rem" color="primary"/>
    </div>
    <div v-else-if="items.length > 0" ref="listRef" class="mention-dropdown-list">
      <template v-for="(item, idx) in items" :key="ChatUtils.mentionItemKey(item, idx)">
        <div v-if="sectionAt(idx)" class="mention-section-header"
             v-text="Translator.translateId(sectionAt(idx) === 'members' ? 'mentionMembers' : 'mentionEntities') ?? sectionAt(idx)"/>
        <div :ref="(el: any) => itemRefs[idx] = el as HTMLElement"
             @mousedown.prevent="emit('select', item)"
             @mouseenter="highlightedIndex = idx"
             class="mention-dropdown-item"
             :class="{'mention-dropdown-item-active': idx === highlightedIndex}">
          <template v-if="item.type === 'user'">
            <div :style="getAvatarStyle(item)" v-text="getMemberInitials(item)"/>
            <span class="tw-font-medium tw-truncate tw-text-sm"
                  v-text="item.name1 || item.name2 || item.code"/>
          </template>
          <template v-else-if="phase === 'record'">
            <div class="tw-flex tw-flex-col tw-min-w-0 tw-flex-1">
              <span class="tw-font-semibold tw-truncate tw-text-sm" v-text="ChatUtils.getRecordName(item)"/>
              <span v-if="ChatUtils.getSecondaryInfo(item)" v-text="ChatUtils.getSecondaryInfo(item)"
                    class="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400 tw-truncate"/>
            </div>
            <span v-if="item.record?.entityType" v-text="ChatUtils.getEntityLabel(item)" :style="ChatUtils.getBadgeStyle(item)"/>
          </template>
          <template v-else>
            <div class="mention-entity-icon">
              <NamaIcon :constant="item.id" defaultIcon="nm-monitoring" class="tw-size-4 active-white"/>
            </div>
            <span class="tw-font-medium tw-truncate tw-text-sm" v-text="item.text"/>
          </template>
        </div>
      </template>
    </div>
    <div v-else v-text=" Translator.translateId('mentionNoResults') ?? 'No results'"
         class="tw-text-center tw-py-3 tw-text-sm tw-text-gray-400 dark:tw-text-gray-500"/>
    <div class="mention-dropdown-footer">
      <span><kbd>&uarr;&darr;</kbd> {{ Translator.translateId('mentionNavigate') ?? 'navigate' }}</span>
      <span><kbd>&crarr;</kbd> {{ Translator.translateId('mentionSelect') ?? 'select' }}</span>
      <span><kbd>Esc</kbd> {{ Translator.translateId('mentionClose') ?? 'close' }}</span>
    </div>
  </div>
</template>
